/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarDays, Loader2, Check, PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CAL_LINK } from "@/lib/content";

// Hardcoded as the default so the form works correctly even if
// REACT_APP_BACKEND_URL doesn't get baked into a given build. Points at the
// custom domain route (favorstudio.agency/api/*), which is the Worker's only
// confirmed-live entry point — workers_dev is disabled on this Worker, so its
// workers.dev URL does not actually resolve. REACT_APP_BACKEND_URL can still
// override this if needed.
const API = `${process.env.REACT_APP_BACKEND_URL || "https://favorstudio.agency"}/api`;

const initialForm = { name: "", business: "", email: "", phone: "", industry: "", message: "" };

export default function BookingModal({ open, onOpenChange }) {
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tab, setTab] = useState("request");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.business) {
      toast.error("Please add your name, business and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        message: date ? `${form.message} | Preferred: ${format(date, "PPP")}`.trim() : form.message,
      };
      await axios.post(`${API}/demo-requests`, payload);
      setDone(true);
      toast.success("Request received — we'll call you to lock in a time.");
    } catch (err) {
      console.error("Demo request failed:", err.response?.data || err.message);
      toast.error("Something went wrong sending your request. Please try again or email contact@favorstudio.agency.");
    } finally {
      setLoading(false);
    }
  };

  // Track whether the calendar tab has been opened at least once (lazy-load iframe).
  const [calOpened, setCalOpened] = useState(false);
  useEffect(() => { if (tab === "calendar") setCalOpened(true); }, [tab]);

  const reset = () => { setForm(initialForm); setDate(null); setDone(false); setTab("request"); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 250); }}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-lg w-[calc(100%-1.5rem)] max-h-[85vh] flex flex-col border-graymid bg-white text-navy-900 p-0 overflow-hidden gap-0"
      >
        <DialogTitle className="sr-only">Book a Live Demo</DialogTitle>
        <DialogDescription className="sr-only">
          Request a quick 15‑minute live demo of the Favor Studio AI Dispatcher.
        </DialogDescription>
        <div className="shrink-0 px-6 pt-6 pb-4 border-b border-graymid bg-graylight">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-navy-900/60 font-semibold">
            <PhoneCall className="w-3.5 h-3.5" /> Book a Live Demo
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-semibold mt-2 tracking-tight text-navy-900">
            Hear her take your calls.
          </h3>
          <p className="text-textgray text-sm mt-1">A quick 15‑minute live demo. No pressure, no tech skills needed.</p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        {done ? (
          <div className="p-8 text-center" data-testid="booking-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-navy-900/8 border border-navy-900/20 flex items-center justify-center mb-4">
              <Check className="w-7 h-7 text-navy-900" />
            </div>
            <h4 className="font-display text-xl font-semibold text-navy-900">You're on the list.</h4>
            <p className="text-textgray text-sm mt-2 max-w-xs mx-auto">
              We'll reach out to <span className="text-navy-900 font-medium">{form.phone}</span> to confirm your demo time. Talk soon.
            </p>
            <Button className="mt-6 bg-navy-900 hover:bg-navy-950 text-white" onClick={() => onOpenChange(false)} data-testid="booking-close-btn">
              Done
            </Button>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="px-6 pt-4">
              <TabsList className="w-full bg-graylight border border-graymid">
                <TabsTrigger value="request" className="flex-1 data-[state=active]:bg-navy-900 data-[state=active]:text-white" data-testid="tab-request">
                  Quick request
                </TabsTrigger>
                <TabsTrigger value="calendar" className="flex-1 data-[state=active]:bg-navy-900 data-[state=active]:text-white" data-testid="tab-calendar">
                  Live calendar
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="request" className="mt-0">
              <form onSubmit={submit} className="p-6 space-y-4" data-testid="booking-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Your name" htmlFor="bk-name">
                    <Input id="bk-name" data-testid="booking-name" value={form.name} onChange={update("name")} placeholder="Mike Davison" className="bg-white border-graymid h-11 md:h-9" />
                  </Field>
                  <Field label="Business" htmlFor="bk-biz">
                    <Input id="bk-biz" data-testid="booking-business" value={form.business} onChange={update("business")} placeholder="Davison & Sons HVAC" className="bg-white border-graymid h-11 md:h-9" />
                  </Field>
                  <Field label="Phone" htmlFor="bk-phone">
                    <Input id="bk-phone" data-testid="booking-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 012‑3456" className="bg-white border-graymid h-11 md:h-9" />
                  </Field>
                  <Field label="Email" htmlFor="bk-email">
                    <Input id="bk-email" data-testid="booking-email" type="email" value={form.email} onChange={update("email")} placeholder="mike@company.com" className="bg-white border-graymid h-11 md:h-9" />
                  </Field>
                </div>

                <Field label="Preferred day (optional)" htmlFor="bk-date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        id="bk-date"
                        data-testid="booking-date-trigger"
                        className="flex w-full items-center gap-2 rounded-md border border-graymid bg-white px-3 h-11 md:h-9 text-base md:text-sm text-left hover:border-navy-900/40 transition-colors"
                      >
                        <CalendarDays className="w-4 h-4 text-navy-900" />
                        {date ? format(date, "PPP") : <span className="text-textlight">Pick a day that suits you</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white border-graymid" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label="Anything we should know? (optional)" htmlFor="bk-msg">
                  <Textarea id="bk-msg" data-testid="booking-message" value={form.message} onChange={update("message")} placeholder="e.g. We miss the most calls on weekends." className="bg-white border-graymid min-h-[96px] md:min-h-[72px]" />
                </Field>

                <Button type="submit" disabled={loading} data-testid="booking-submit" className="w-full h-11 bg-navy-900 hover:bg-navy-950 text-white text-base font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request my free demo"}
                </Button>
                <p className="text-[11px] text-textlight text-center">We'll never share your details. Cancel anytime.</p>
              </form>
            </TabsContent>

            <TabsContent value="calendar" className="mt-0">
              <div className="p-4">
                <div data-testid="cal-embed" className="w-full h-[560px] rounded-md overflow-hidden bg-white border border-graymid">
                  {calOpened ? (
                    <iframe
                      title="Book a demo with Favor Studio"
                      src={`https://cal.com/${CAL_LINK}?embed=true&theme=light&primaryColor=2C3E50&layout=month_view`}
                      allow="camera; microphone; autoplay; clipboard-write"
                      className="w-full h-full border-0"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center text-textlight text-sm px-6">
                        <CalendarDays className="w-6 h-6 mx-auto mb-2 text-navy-900" />
                        Loading live calendar…
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, htmlFor, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-textgray">{label}</Label>
    {children}
  </div>
);
